import {
  Center,
  HStack,
  Progress,
  Spacer,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
  Flex,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { useReUploadLoanTapeMutation } from 'types/dashboard-core-gqlTypes.generated.d';

import { ReceivableReconciliation } from './ReceivableReconciliation';
import { isAdminDashboard } from 'packages/common-frontend/utils';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { colors } from '~/packages/common-frontend/colors';
import { InfoIconButton } from '~/packages/common-frontend/components/IconButton';
import { useSlugToName } from '~/packages/dataPipeline/hooks/slugToNameContext';
import {
  useUploadContextMeshRunParams,
  useUploadContext,
} from '~/packages/dataPipeline/hooks/UploadContext';
import { useInputNode } from '~/packages/dataPipeline/hooks/useInputNode';
import { getFieldInfo } from '~/packages/dataPipeline/ui/details/utils';
import { UploadStatusIndicator } from '~/packages/dataPipeline/ui/uploads/pipelinePicker/UploadStateIndicator';
import { UploadModal } from '~/packages/dataPipeline/ui/uploads/UploadModal';
import { DataTapeFormatModal } from '~/packages/manualDataTapeUploads/ui/DataTapeFormatModal';
import { UploadCashBalanceForm } from '~/packages/manualDataTapeUploads/ui/UploadCashBalanceForm';
import { UploadDataTapeForm } from '~/packages/manualDataTapeUploads/ui/UploadDataTapeForm';

const AdminRouteParamsStruct = z.object({
  organizationId: z.string({ message: 'Missing URL parameter' }),
});

export const UploadSingleInput = ({
  meshInput,
  closeDrawer,
}: {
  meshInput: DataPipeline.MeshInput;
  closeDrawer: () => void;
}) => {
  const flags = useFeatureFlags();

  const meshRunParams = useUploadContextMeshRunParams();

  const dataTape = useMemo(() => {
    const { csvTape, xlsxTape } = getFieldInfo({
      graph: {
        artifacts: meshRunParams.meshDocument.artifacts,
        dependencies: meshRunParams.meshDocument.dependencies.map((dep) => ({
          inputArtifactId: dep.source,
          outputArtifactId: dep.target,
        })),
      },
      artifactId: meshInput.artifactId,
    });
    return xlsxTape ?? csvTape;
  }, [
    meshRunParams.meshDocument.artifacts,
    meshRunParams.meshDocument.dependencies,
    meshInput.artifactId,
  ]);

  const {
    rdfv: { reportingDate, fileVersion },
    meshDocument: { borrowerOrganizationId, implementationId },
  } = meshRunParams;

  const {
    upload,
    uploadNeeded,
    progress,
    instanceState,
    cashUploadStarted,
    cashUploadEnded,
    errorMessage,
    sanitized,
  } = useInputNode({
    artifactId: meshInput.artifactId,
  });

  const currentArtifactId = meshInput.artifactId;
  const currentArtifact = meshRunParams.meshDocument.artifacts.find(
    (artifact) => artifact.id === currentArtifactId,
  );
  finleyAssert(
    currentArtifact,
    'Expected to find current artifact in mesh document',
  );

  const uploadModal = useDisclosure();
  const dataTapeFormatModal = useDisclosure();
  const { cfSlugToName, lpSlugToName } = useSlugToName();

  const textColor = useColorModeValue(colors.black[70], colors.white[700]);
  const backgroundColor = useColorModeValue(
    colors.background[50],
    colors.gray[700],
  );
  const borderColor = useColorModeValue(colors.gray[100], '#ffffff29');
  const errorTextColor = useColorModeValue('red.600', 'red.300');

  const mainText = meshInput.friendlyName;
  const otherTexts: string[] = [];
  if (
    meshInput.creditFacilitySlug &&
    // we do not want to render CFSlug for valley bank inputs.
    meshInput.creditFacilitySlug !== 'valleyBankBorrowersValleyBank1'
  ) {
    otherTexts.push(cfSlugToName(meshInput.creditFacilitySlug));
  }
  if ('loanProductSlug' in meshInput && meshInput.loanProductSlug) {
    otherTexts.push(lpSlugToName(meshInput.loanProductSlug));
  }

  const { completeRecord, instances } = useUploadContext();

  const [
    areAllReceivablesReconciled,
    setAreAllReceivablesReconciled,
  ] = useState<boolean>(false);

  const [kickedOffNewPipeline, setKickedOffNewPipeline] = useState<boolean>(
    false,
  );

  const isUploadRetryCandidate: boolean =
    instanceState === 'success' &&
    !!completeRecord &&
    completeRecord.meshRunRecord.status === 'error' &&
    areAllReceivablesReconciled;

  const [
    reUploadLoanTape,
    { loading: reUploadingLoanTape },
  ] = useReUploadLoanTapeMutation({
    onCompleted: () => {
      setKickedOffNewPipeline(true);
    },
  });

  const { user } = useUserAuthOrThrow();
  const params = useParams();

  const organizationId = isAdminDashboard()
    ? AdminRouteParamsStruct.parse(params).organizationId // on admin dash, pick selected org
    : user.organization.id; // on core dashboard, pick user org

  return (
    <Flex direction="column" width="100%">
      <VStack
        key={meshInput.artifactId}
        as="span"
        alignItems="flex-start"
        spacing={1}
        borderRadius="8px"
        padding="16px"
        border={`1px solid ${borderColor}`}
        bg={backgroundColor}
      >
        <HStack width="100%" justifyContent="space-between">
          <Text
            as="span"
            color={textColor}
            fontSize="md"
            fontWeight="bold"
            lineHeight="1.2rem"
          >
            {mainText}
          </Text>
          {meshInput.type === 'file' && dataTape && (
            <>
              <DataTapeFormatModal
                isOpen={dataTapeFormatModal.isOpen}
                onClose={dataTapeFormatModal.onClose}
                dataTape={dataTape}
              />
              <InfoIconButton
                ariaLabel="Data Tape Format"
                label="Data Tape Format"
                onClick={dataTapeFormatModal.onOpen}
                fontSize="xl"
                color={colors.blue[400]}
              />
            </>
          )}
          <Spacer />
          <UploadStatusIndicator
            status={instanceState}
            sanitized={sanitized}
            onClick={() => uploadNeeded && uploadModal.onOpen()}
            isUploadRetryCandidate={isUploadRetryCandidate}
            retryUpload={() => {
              const currentArtifactInstance = instances.find(
                (instance) => instance.artifactId === meshInput.artifactId,
              );
              finleyAssert(
                currentArtifactInstance,
                'Expected to find current artifact instance when reupload loan tape',
              );
              reUploadLoanTape({
                variables: {
                  artifactId: meshInput.artifactId,
                  implementationId,
                  reportingDate,
                  fileVersion,
                  currentArtifactInstanceId: currentArtifactInstance.id,
                },
              });
            }}
            reUploadingLoanTape={reUploadingLoanTape || kickedOffNewPipeline}
          />
          {meshInput.type === 'file' && (
            <UploadModal
              modalHeaderText="Upload Tape"
              isOpen={uploadModal.isOpen}
              onClose={uploadModal.onClose}
            >
              <UploadDataTapeForm
                allowedExtensions={[meshInput.extension]}
                onClose={uploadModal.onClose}
                handleFile={(file) => upload(file)}
              />
            </UploadModal>
          )}
          {meshInput.type === 'cashBalance' && (
            <UploadModal
              modalHeaderText="Upload Cash Balance"
              isOpen={uploadModal.isOpen}
              onClose={uploadModal.onClose}
            >
              <Text fontWeight="bold" mb="1rem" fontSize="xl">
                {meshInput.creditFacilityId}
              </Text>
              <UploadCashBalanceForm
                organizationId={borrowerOrganizationId}
                creditFacilityId={meshInput.creditFacilityId}
                reportingDate={reportingDate}
                fileVersion={fileVersion}
                accept={{
                  'image/png': ['.png'],
                }}
                onClose={uploadModal.onClose}
                onLoading={cashUploadStarted}
                onSubmit={async () => {}}
                onSuccess={cashUploadEnded}
              />
            </UploadModal>
          )}
        </HStack>
        <Text as="span" color={textColor} lineHeight="1.2rem">
          {otherTexts.join(', ')}
        </Text>
        {errorMessage && (
          <Text as="span" color={errorTextColor} lineHeight="1.2rem">
            {errorMessage}
          </Text>
        )}
        {progress && (
          <Center w="100%">
            <Progress colorScheme="green" value={progress} size="sm" w="100%" />
          </Center>
        )}
      </VStack>
      {flags.displayUnassignmentUi &&
        currentArtifact.type === 'rawLoanTape' &&
        meshInput.type === 'file' &&
        meshInput.loanProductSlug &&
        meshInput.creditFacilitySlug &&
        organizationId === borrowerOrganizationId && (
          <ReceivableReconciliation
            loanProductSlug={meshInput.loanProductSlug}
            creditFacilitySlug={meshInput.creditFacilitySlug}
            implementationId={implementationId}
            setAreAllReceivablesReconciled={setAreAllReceivablesReconciled}
            kickedOffNewPipeline={kickedOffNewPipeline}
            closeDrawer={closeDrawer}
          />
        )}
    </Flex>
  );
};
